import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { COMPARISON_TABLE_DAYE_LOGO } from '@constants';
import { PageSlice, PageSliceComparisonTable, ProductCode } from '@contracts';
import { TableData } from '@data/tablesData';
import { getSubscriptionPlans } from '@utils/getSubscriptionPlans';
import { getProductPrice, roundNumber } from '@utils/price';
import { getDiscountPercent } from '@utils/subscriptions';

interface Props {
  slice: PageSlice;
  data: TableData;
}

const ComparisonTable: React.FC<Props> = ({ slice, data }) => {
  const primary = slice.primary as PageSliceComparisonTable;
  const { state } = useContext(Context);
  const { currency, country } = state;
  const [price, setPrice] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    handleFetchProductPrice();
  }, []);

  const generatePrice = (priceInput: string) => {
    const priceTextArr = priceInput.split('-');
    return priceTextArr.map((priceText, index) => <span key={index}>{priceText}</span>);
  };

  const handleFetchProductPrice = async () => {
    const getPrice = await getProductPrice(ProductCode.Proviotics, currency, country);
    const plans = await getSubscriptionPlans(ProductCode.Proviotics);
    const maxDiscount = plans?.reduce((max: number, subscription) => Math.max(max, getDiscountPercent(subscription, country)), 0);
    const basePrice = getPrice.price;
    const discountedPrice = roundNumber(basePrice - (basePrice * maxDiscount) / 100);
    setPrice(discountedPrice);
  };

  return (
    <Wrapper>
      <Container>
        <TableHeader>
          <FirstColumn dangerouslySetInnerHTML={{ __html: data.header[0] }} />
          <BorderColumn $borderTop={true}>
            <img src={COMPARISON_TABLE_DAYE_LOGO} />
          </BorderColumn>
          <Column dangerouslySetInnerHTML={{ __html: data.header[2] }} />
          <Column dangerouslySetInnerHTML={{ __html: data.header[3] }} />
          {data.header[4] && <Column dangerouslySetInnerHTML={{ __html: data.header[4] }} />}
        </TableHeader>

        {Object.keys(data.body).map((row, index) => {
          // creating image variables so we can render them on browser
          const Image1 = data.body[row][1];
          const Image2 = data.body[row][2];
          const Image3 = data.body[row][3];
          const Image4 = data.body[row][4];
          const rowCell1 = data.body[row][1];
          return (
            <TableRow key={index} $index={index}>
              <FirstColumn>
                <span>{data.body[row][0]}</span>
              </FirstColumn>
              <BorderColumn $borderBottom={index + 1 === Object.keys(data.body).length && !data.hasBottomRow}>
                <span>{typeof rowCell1 === 'string' ? rowCell1 : <Image1 />}</span>
              </BorderColumn>
              <Column>
                <span>{typeof rowCell1 === 'string' ? data.body[row][2] : <Image2 />}</span>
              </Column>
              <Column>
                <span>{typeof rowCell1 === 'string' ? data.body[row][3] : <Image3 />}</span>
              </Column>
              {Image4 && (
                <Column>
                  <span>{typeof rowCell1 === 'string' ? data.body[row][4] : <Image4 />}</span>
                </Column>
              )}
            </TableRow>
          );
        })}
        {data.hasBottomRow && (
          <TableHeader>
            <FirstColumn>
              <span>{t('components.cost-per-month').toUpperCase()}</span>
            </FirstColumn>
            <BorderColumn $borderBottom={true}>
              <span>
                {currency.symbol}
                {price?.toFixed(2)}
              </span>
              <span>
                {currency.symbol}
                {(price / 30)?.toFixed(2)}/pill{' '}
              </span>
            </BorderColumn>
            <Column>{generatePrice(primary.price_optibac?.text)}</Column>
            <Column>{generatePrice(primary.price_garden_of_life?.text)}</Column>
          </TableHeader>
        )}
      </Container>
    </Wrapper>
  );
};

export default ComparisonTable;

interface StyleProps {
  $index?: number;
  $borderTop?: boolean;
  $borderBottom?: boolean;
}

const Wrapper = styled.div`
  overflow: scroll;

  @media (max-width: 1220px) {
    align-self: center;
  }

  @media (max-width: 768px) {
    align-self: auto;
    padding: 0 20px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 2px solid ${Colors.sky};
  border-right: 2px solid ${Colors.sky};
  border-bottom: 2px solid ${Colors.sky};
  color: ${Colors.navyBlue};
  max-width: 700px;
  flex: 1;
  overflow: auto;

  & > div {
    height: 60px;
    align-items: center;
  }

  div span {
    max-width: 85%;
  }

  @media (max-width: 540px) {
    width: 465px;
    div span {
      max-width: 100%;
      padding-inline: 5px;
    }
  }
`;

const TableHeader = styled.div`
  display: flex;
  background: ${Colors.sky};
  text-transform: uppercase;
  font-family: BrownStd;
  font-size: 14px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: 1px;
  text-align: center;

  span:nth-child(2) {
    text-transform: none;
    font-family: BrownStd;
    font-size: 12px;
    font-weight: 400;
    line-height: 21px;
    letter-spacing: 0;
    text-align: center;
  }

  @media (max-width: 540px) {
    font-size: 12px;
  }
`;

const TableRow = styled.div<StyleProps>`
  display: flex;
  font-family: BrownStd;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: center;
  background: ${(props) => (props.$index && props.$index % 2 === 1 ? '#FCF3DF' : Colors.cream)};

  @media (max-width: 540px) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const Column = styled.div`
  width: 110px;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
  flex-direction: column;
  line-height: 130%;

  @media (max-width: 540px) {
    width: 80px;
  }
`;

const FirstColumn = styled(Column)`
  width: 250px;
  display: flex;
  justify-content: center;
  border-right: 2px solid ${Colors.gold};
  height: 100%;
  align-items: center;
  @media (max-width: 540px) {
    width: 140px;
  }
`;

const BorderColumn = styled(Column)<StyleProps>`
  ${(props) => {
    let border;
    if (props.$borderTop) border = `border-top: 2px solid ${Colors.gold};`;
    if (props.$borderBottom) border = `border-bottom: 2px solid ${Colors.gold};`;
    return `border-right: 2px solid ${Colors.gold}; ${border}`;
  }};
`;
